/**
 * The caller's conversations in the selected project.
 *
 * Keyed on the project with the token read from a ref — the same shape as
 * `useProjects`, and for the same reason: the single-session guard rotates the
 * access token on focus, visibility and every realtime reconnect, and keying on
 * it would refetch the list each time for no new information.
 *
 * The list is reloaded whenever a turn settles, so a conversation created by
 * the agent mid-stream appears in History without the user having to switch
 * projects to see it. That makes most loads *refreshes*, which is why the state
 * machine distinguishes them from a first fetch below.
 *
 * Family Builder conversations are left out: they belong to the family agent
 * and are opened from their component, never from History (v2 filtered them
 * the same way, by title).
 *
 * `access-lost` is a state of its own rather than a message inside `error`. A
 * 403 on this endpoint means the caller was unassigned from the project since
 * they selected it, which is a different thing to tell someone than "the request
 * failed" — and rendering it as an empty list, which is what happens if it is
 * swallowed, reads as "no conversations yet".
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { API_BASE_URL, isConfigured } from "../lib/config";
import { getThreads, statusOf, type Thread } from "../lib/threads";
import { isFamilyBuilderThread } from "../family-builder/model";

export type ThreadsState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "access-lost" }
  | { kind: "error"; message: string }
  | { kind: "loaded"; threads: Thread[] };

export function useThreads(
  projectId: string | null,
  token: string | null,
): ThreadsState & { reload: () => void } {
  const [state, setState] = useState<ThreadsState>({ kind: "idle" });
  /** Bumped by `reload`, so a failure is recoverable without reloading the app. */
  const [attempt, setAttempt] = useState(0);

  const tokenRef = useRef(token);
  tokenRef.current = token;

  /**
   * The project the list currently on screen belongs to.
   *
   * What separates a **background refresh** from a first load. Every settled
   * turn reloads this list, and blanking it to a spinner each time would make an
   * open History drawer flash on every answer — while showing the *previous*
   * project's threads during a project switch would be worse than a spinner.
   */
  const loadedFor = useRef<string | null>(null);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    if (!projectId || !isConfigured()) {
      loadedFor.current = null;
      setState({ kind: "idle" });
      return;
    }

    let cancelled = false;
    const refreshing = loadedFor.current === projectId;
    if (!refreshing) setState({ kind: "loading" });

    getThreads(tokenRef.current, projectId, { apiBaseUrl: API_BASE_URL() })
      .then((threads) => {
        if (cancelled) return;
        loadedFor.current = projectId;
        setState({ kind: "loaded", threads: threads.filter((thread) => !isFamilyBuilderThread(thread.title)) });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        // Losing access wins either way: the caller was unassigned from this
        // project, and a list they may no longer see is not worth keeping on
        // screen because the refresh happened to be a background one.
        if (statusOf(error) === 403) {
          loadedFor.current = null;
          setState({ kind: "access-lost" });
          return;
        }
        // A background refresh that fails keeps what is already there. The list
        // is stale by one turn; replacing a working set of conversations with an
        // error page because a refetch nobody asked for went wrong is worse.
        if (refreshing) return;
        setState({
          kind: "error",
          message: error instanceof Error ? error.message : String(error),
        });
      });

    return () => {
      cancelled = true;
    };
  }, [projectId, attempt]);

  return { ...state, reload };
}
